import type { UserProfile } from "@/types/user";
import { TbSearch, TbUser, TbChevronDown } from "react-icons/tb";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RBACService } from "@/services/rbacService";
import { usePermissions } from "@/hooks/usePermissions";
import toast from "react-hot-toast";
import { userKeys } from "@/hooks/useUsers";
import * as Select from "@radix-ui/react-select";

interface UserManagerProps {
  users: UserProfile[];
  userProfile?: UserProfile | null;
  error?: string | null;
}

export const UserManager: React.FC<UserManagerProps> = ({
  users,
  userProfile,
  error,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [managingUserId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const {
    hasPermission,
    hasRole,
    isLoading: permissionsLoading,
  } = usePermissions();

  const canManageRoles =
    hasPermission("manage_user_roles") ||
    hasPermission("manage_roles") ||
    hasRole("Super Admin");

  // Always fetch roles (for display), not just when canManageRoles
  const { data: availableRoles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: RBACService.getRoles,
    staleTime: 60000, // Cache for 1 minute
  });

  // Combined loading state for showing skeleton
  const isRoleColumnLoading = permissionsLoading || rolesLoading;

  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: userKeys.all });
    queryClient.invalidateQueries({ queryKey: userKeys.allIncludingAdmins });
    if (managingUserId) {
      queryClient.invalidateQueries({
        queryKey: userKeys.byId(managingUserId),
      });
    }
  };

  const assignRoleMutation = useMutation({
    mutationFn: async ({
      user_id,
      role_id,
    }: {
      user_id: string;
      role_id: string;
    }) => {
      // First, remove all existing roles for this user
      const userToUpdate = users.find((u) => u.id === user_id);
      if (userToUpdate?.rbac_roles && userToUpdate.rbac_roles.length > 0) {
        for (const existingRole of userToUpdate.rbac_roles) {
          await RBACService.removeRoleFromUser(user_id, existingRole.id);
        }
      }
      // Then assign the new role
      return RBACService.assignRoleToUser({ user_id, role_id });
    },
    onSuccess: () => {
      invalidateUsers();
      toast.success("Role assigned successfully");
    },
    onError: (err: Error) => {
      toast.error(`Failed to assign role: ${err.message}`);
    },
  });

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    return users.filter((user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  return (
    <div className="flex flex-col items-start w-full overflow-y-auto min-h-[300px] md:min-h-[400px] max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-350px)] p-4 rounded-[10px] border border-gray-100 text-sm">
      <div className="space-y-2 w-full">
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        {/* Search Bar */}
        <div className="flex flex-col items-start gap-1 w-full rounded-lg">
          <div className="flex p-[8px] items-center gap-3 w-full rounded-[10px] bg-[#F3F4F6]">
            <div className="flex justify-center items-center gap-2 flex-1">
              <TbSearch className="w-4 h-4 text-[#828282]" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
              />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="w-full bg-white overflow-visible">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 rounded-lg">
                <th className="px-2.5 py-3 text-left text-[#444444] font-['Open_Sans'] text-[14px] font-semibold rounded-l-lg">
                  Email
                </th>
                <th className="px-0 py-2 text-left text-[#444444] font-['Open_Sans'] text-[13px] font-semibold">
                  <div className="w-[72px]">Created</div>
                </th>
                <th className="px-5 py-3 text-right text-[#444444] font-['Open_Sans'] text-[14px] font-semibold rounded-r-lg w-[150px]">
                  Role
                </th>
              </tr>
            </thead>
            <tbody className="simple-scrollbar">
              {filteredUsers.map((user) => {
                const isCurrentUser = user.id === userProfile?.id;

                return (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-2.5 py-2.5">
                      <div className="flex flex-col gap-2.5">
                        <span className="justify-start text-neutral-700 text-sm font-['Open_Sans'] leading-tight">
                          {user.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-0 py-2">
                      <div className="flex items-center">
                        <span className="justify-start text-neutral-700 text-xs font-['Open_Sans'] leading-tight">
                          {new Date(user.created_at).toLocaleDateString(
                            "en-GB"
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-0 py-2 text-right w-[150px]">
                      <div className="flex items-center justify-end">
                        {isRoleColumnLoading ? (
                          <div className="animate-pulse bg-gray-200 rounded-[10px] h-[36px] w-[130px]" />
                        ) : isCurrentUser ? (
                          <div className="flex items-center gap-2">
                            {user.rbac_roles && user.rbac_roles.length > 0 && (
                              <span className="bg-[rgba(55,71,149,0.08)] text-[#374795] px-2 py-0.5 rounded-[16px] text-[10px] font-bold font-['Open_Sans'] uppercase tracking-tight whitespace-nowrap">
                                {user.rbac_roles[0].name}
                              </span>
                            )}
                            <div className="bg-[rgba(55,71,149,0.06)] flex items-center justify-center px-3 py-2 rounded-[10px]">
                              <span className="font-['Open_Sans'] font-bold text-[#374795] text-[12px] leading-[1.3] tracking-[0.12px]">
                                ME
                              </span>
                            </div>
                          </div>
                        ) : canManageRoles ? (
                          <Select.Root
                            value={user.rbac_roles?.[0]?.id ?? undefined}
                            onValueChange={(roleId) => {
                              const role = availableRoles.find(
                                (r) => r.id === roleId
                              );
                              if (role) {
                                assignRoleMutation.mutate({
                                  user_id: user.id,
                                  role_id: role.id,
                                });
                              }
                            }}
                          >
                            <Select.Trigger className="border border-gray-100 inline-flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] hover:bg-gray-50 transition-colors min-w-[130px] font-['Open_Sans'] font-semibold text-[#374795] text-[12px]">
                              <Select.Value placeholder="Select Role" />
                              <Select.Icon>
                                <TbChevronDown className="w-4 h-4 text-[#374795]" />
                              </Select.Icon>
                            </Select.Trigger>
                            <Select.Portal>
                              <Select.Content
                                className="overflow-hidden bg-white rounded-[10px] shadow-lg border border-gray-200 z-50"
                                position="popper"
                                sideOffset={5}
                              >
                                <Select.Viewport className="p-1">
                                  {rolesLoading ? (
                                    <div className="px-3 py-2 text-[12px] text-[#828282] font-['Open_Sans']">
                                      Loading roles...
                                    </div>
                                  ) : availableRoles.length === 0 ? (
                                    <div className="px-3 py-2 text-[12px] text-[#828282] font-['Open_Sans']">
                                      No roles available
                                    </div>
                                  ) : (
                                    availableRoles.map((role) => (
                                      <Select.Item
                                        key={role.id}
                                        value={role.id}
                                        className="relative flex items-center px-3 py-2 text-[12px] font-['Open_Sans'] text-[#444444] rounded-[6px] hover:bg-gray-50 cursor-pointer outline-none data-[highlighted]:bg-gray-50"
                                      >
                                        <Select.ItemText>
                                          {role.name}
                                        </Select.ItemText>
                                      </Select.Item>
                                    ))
                                  )}
                                </Select.Viewport>
                              </Select.Content>
                            </Select.Portal>
                          </Select.Root>
                        ) : // Show role badge for users without manage permission
                        user.rbac_roles && user.rbac_roles.length > 0 ? (
                          <span className="bg-[rgba(55,71,149,0.08)] text-[#374795] px-2 py-0.5 rounded-[16px] text-[10px] font-bold font-['Open_Sans'] uppercase tracking-tight whitespace-nowrap">
                            {user.rbac_roles[0].name}
                          </span>
                        ) : (
                          <span className="text-[12px] text-[#828282] font-['Open_Sans']">
                            No role
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <TbUser size={48} className="mx-auto mb-4 text-gray-300" />
            {searchTerm
              ? "No users found matching your search."
              : "No users found."}
          </div>
        )}
      </div>
    </div>
  );
};
